'use client'

import { PrimitiveIcon, UIIcon } from '@gluestack-ui/icon'
import { createInput } from '@gluestack-ui/input'
import type { VariantProps } from '@gluestack-ui/nativewind-utils'
import { tva } from '@gluestack-ui/nativewind-utils/tva'
import { useStyleContext, withStyleContext } from '@gluestack-ui/nativewind-utils/withStyleContext'
import { cssInterop } from 'nativewind'
import React from 'react'
import { Pressable, TextInput, View } from 'react-native'

const SCOPE = 'INPUT'

const UIInput = createInput({
  Root: withStyleContext(View, SCOPE),
  Icon: UIIcon,
  Slot: Pressable,
  Input: TextInput
})

cssInterop(PrimitiveIcon, {
  className: {
    target: 'style',
    nativeStyleToProp: {
      height: true,
      width: true,
      fill: true,
      color: 'classNameColor',
      stroke: true
    }
  }
})

const inputStyle = tva({
  base: 'flex-row content-center items-center overflow-hidden border-background-300 data-[focus=true]:border-primary-700 data-[focus=true]:hover:border-primary-700 data-[hover=true]:border-outline-400 data-[disabled=true]:opacity-40 data-[disabled=true]:hover:border-background-300',

  variants: {
    size: {
      '2xl': 'h-14',
      xl: 'h-12',
      lg: 'h-11',
      md: 'h-10',
      sm: 'h-9'
    },

    variant: {
      underlined:
        'rounded-none border-b data-[invalid=true]:border-b-2 data-[invalid=true]:border-error-700 data-[invalid=true]:hover:border-error-700 data-[invalid=true]:data-[focus=true]:hover:border-error-700 data-[invalid=true]:data-[focus=true]:border-error-700 data-[invalid=true]:data-[disabled=true]:hover:border-error-700',

      outline:
        'rounded border data-[invalid=true]:border-error-700 data-[invalid=true]:hover:border-error-700 data-[invalid=true]:web:ring-1 data-[invalid=true]:web:ring-inset data-[invalid=true]:web:ring-indicator-error data-[invalid=true]:data-[focus=true]:hover:web:ring-inset data-[invalid=true]:data-[focus=true]:hover:web:ring-indicator-error data-[invalid=true]:data-[focus=true]:hover:web:ring-1 data-[invalid=true]:data-[focus=true]:hover:border-error-700 data-[invalid=true]:data-[focus=true]:border-error-700 data-[invalid=true]:data-[disabled=true]:hover:web:ring-inset data-[invalid=true]:data-[disabled=true]:hover:web:ring-indicator-error data-[invalid=true]:data-[disabled=true]:hover:web:ring-1 data-[invalid=true]:data-[disabled=true]:hover:border-error-700 data-[focus=true]:web:ring-1 data-[focus=true]:web:ring-inset data-[focus=true]:web:ring-indicator-primary',

      rounded:
        'rounded-full border data-[invalid=true]:border-error-700 data-[invalid=true]:hover:border-error-700 data-[invalid=true]:web:ring-1 data-[invalid=true]:web:ring-inset data-[invalid=true]:web:ring-indicator-error data-[invalid=true]:data-[focus=true]:hover:web:ring-inset data-[invalid=true]:data-[focus=true]:hover:web:ring-indicator-error data-[invalid=true]:data-[focus=true]:hover:web:ring-1 data-[invalid=true]:data-[focus=true]:hover:border-error-700 data-[invalid=true]:data-[focus=true]:border-error-700 data-[invalid=true]:data-[disabled=true]:hover:web:ring-inset data-[invalid=true]:data-[disabled=true]:hover:web:ring-indicator-error data-[invalid=true]:data-[disabled=true]:hover:web:ring-1 data-[invalid=true]:data-[disabled=true]:hover:border-error-700 data-[focus=true]:web:ring-1 data-[focus=true]:web:ring-inset data-[focus=true]:web:ring-indicator-primary'
    }
  }
})

const inputIconStyle = tva({
  base: 'items-center justify-center fill-none text-typography-400',
  parentVariants: {
    size: {
      '2xs': 'h-3 w-3',
      xs: 'h-3.5 w-3.5',
      sm: 'h-4 w-4',
      md: 'h-[18px] w-[18px]',
      lg: 'h-5 w-5',
      xl: 'h-6 w-6'
    }
  }
})

const inputSlotStyle = tva({
  base: 'items-center justify-center web:disabled:cursor-not-allowed'
})

const inputFieldStyle = tva({
  base: 'ios:leading-[0px] h-full flex-1 px-3 py-0 font-poppinsRegular text-typography-900 placeholder:text-typography-500 web:cursor-text web:data-[disabled=true]:cursor-not-allowed',

  parentVariants: {
    variant: {
      underlined: 'px-0 web:outline-none web:outline-0',
      outline: 'web:outline-none web:outline-0',
      rounded: 'px-4 web:outline-none web:outline-0'
    },

    size: {
      '2xs': 'text-2xs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-lg',
      '2xl': 'text-lg',
      '3xl': 'text-xl',
      '4xl': 'text-xl',
      '5xl': 'text-xl',
      '6xl': 'text-xl'
    }
  }
})

type InputProps = React.ComponentProps<typeof UIInput> & VariantProps<typeof inputStyle> & { className?: string }
const Input = React.forwardRef<React.ComponentRef<typeof UIInput>, InputProps>(
  ({ className, variant = 'outline', size = 'md', ...props }, ref) => {
    return (
      <UIInput
        ref={ref}
        {...props}
        accessible={false}
        className={inputStyle({ variant, size, class: className })}
        context={{ variant, size }}
      />
    )
  }
)

type InputIconProps = React.ComponentProps<typeof UIInput.Icon> &
  VariantProps<typeof inputIconStyle> & {
    className?: string
    height?: number
    width?: number
  }

const InputIcon = React.forwardRef<React.ComponentRef<typeof UIInput.Icon>, InputIconProps>(
  ({ className, size, ...props }, ref) => {
    const { size: parentSize } = useStyleContext(SCOPE)

    if (typeof size === 'number') {
      return (
        <UIInput.Icon
          ref={ref}
          {...props}
          accessible={false}
          className={inputIconStyle({ class: className })}
          size={size}
        />
      )
    } else if ((props.height !== undefined || props.width !== undefined) && size === undefined) {
      return (
        <UIInput.Icon
          ref={ref}
          {...props}
          accessible={false}
          className={inputIconStyle({ class: className })}
        />
      )
    }
    return (
      <UIInput.Icon
        ref={ref}
        {...props}
        accessible={false}
        className={inputIconStyle({
          parentVariants: {
            size: parentSize
          },
          class: className
        })}
      />
    )
  }
)

type InputSlotProps = React.ComponentProps<typeof UIInput.Slot> &
  VariantProps<typeof inputSlotStyle> & { className?: string }

const InputSlot = React.forwardRef<React.ComponentRef<typeof UIInput.Slot>, InputSlotProps>(
  ({ className, ...props }, ref) => {
    return (
      <UIInput.Slot
        ref={ref}
        {...props}
        accessible={false}
        className={inputSlotStyle({
          class: className
        })}
      />
    )
  }
)

type InputFieldProps = React.ComponentProps<typeof UIInput.Input> &
  VariantProps<typeof inputFieldStyle> & { className?: string }

const InputField = React.forwardRef<React.ComponentRef<typeof UIInput.Input>, InputFieldProps>(
  ({ className, ...props }, ref) => {
    const { variant: parentVariant, size: parentSize } = useStyleContext(SCOPE)

    return (
      <UIInput.Input
        ref={ref}
        {...props}
        className={inputFieldStyle({
          parentVariants: {
            variant: parentVariant,
            size: parentSize
          },
          class: className
        })}
      />
    )
  }
)

Input.displayName = 'Input'
InputIcon.displayName = 'InputIcon'
InputSlot.displayName = 'InputSlot'
InputField.displayName = 'InputField'

export {
  Input,
  type InputProps,
  InputField,
  type InputFieldProps,
  InputIcon,
  type InputIconProps,
  InputSlot,
  type InputSlotProps
}
